import { useEffect, useMemo, useState } from "react";
import { AlertCircle, TrendingUp } from "lucide-react";
import { Label } from "@/components/ui/label";
import { groupDigits, parseNumber } from "@/lib/format";
import { HelperTooltip } from "@/components/calculator/fields/HelperTooltip";
import { useFieldRef } from "@/components/calculator/fields/useFieldRef";

/** Whole-number input (transaction counts). Grouped as you type, no negatives. */
export const NumberField = ({ field, value, onChange, error, autoFocus = false, registerRef }) => {
  const inputRef = useFieldRef({ path: field.path, registerRef, autoFocus });
  const [text, setText] = useState(
    value === null || value === undefined || value === "" ? "" : groupDigits(value),
  );

  // Re-format only when the stored number and what is on screen actually differ,
  // so a saved answer coming back in is shown grouped without fighting the
  // caret while somebody is mid-type.
  useEffect(() => {
    const incoming = value === null || value === undefined || value === "" ? "" : String(value);
    if (parseNumber(text) !== parseNumber(incoming)) {
      setText(incoming === "" ? "" : groupDigits(incoming));
    }
  }, [value, text]);

  const inputId = `field-${field.path.replace(/\./g, "-")}`;
  const describedBy = useMemo(
    () => [field.help ? `${inputId}-help` : null, error ? `${inputId}-error` : null]
      .filter(Boolean)
      .join(" ") || undefined,
    [field.help, error, inputId],
  );

  return (
    <div data-testid={`number-field-${field.path}`}>
      <div className="flex items-start gap-2">
        <Label htmlFor={inputId} className="text-[15px] font-semibold leading-snug text-foreground">
          {field.label}
        </Label>
        <HelperTooltip text={field.tooltip} />
      </div>
      {field.sublabel ? (
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{field.sublabel}</p>
      ) : null}

      <div className="relative mt-3">
        <input
          id={inputId}
          ref={inputRef}
          data-testid={`number-input-${field.path}`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={text}
          onChange={(event) => {
            const cleaned = event.target.value.replace(/[^0-9,]/g, "");
            const formatted = groupDigits(cleaned);
            setText(formatted);
            const parsed = parseNumber(formatted);
            onChange(parsed === null ? "" : parsed);
          }}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={describedBy}
          placeholder="0"
          className={`tt-no-spin h-14 w-full rounded-lg border bg-white px-4 text-right font-amount text-xl font-semibold text-foreground outline-none transition-[border-color,box-shadow] duration-150 placeholder:font-normal placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-primary/25 ${
            error ? "border-destructive" : "border-input"
          }`}
        />
        {field.unit ? (
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            {field.unit}
          </span>
        ) : null}
      </div>

      {field.emphasis ? (
        <p
          id={`${inputId}-help`}
          data-testid={`number-emphasis-${field.path}`}
          className="mt-2.5 flex items-start gap-2 rounded-lg border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-3 py-2.5 text-[13px] leading-relaxed text-[color:var(--tt-teal-deep)]"
        >
          <TrendingUp className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{field.help}</span>
        </p>
      ) : field.help ? (
        <p id={`${inputId}-help`} className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
          {field.help}
        </p>
      ) : null}

      {error ? (
        <p
          id={`${inputId}-error`}
          data-testid={`field-error-${field.path}`}
          role="alert"
          className="mt-2 flex items-start gap-1.5 text-[13px] font-medium text-destructive"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
};
